import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export function LegalLayout({ overline, title, children }) {
  return (
    <div className="bg-[#F7F5F0] min-h-screen">
      <div className="bg-[#0A0A0B] relative"><Navbar dark /><div className="h-24" /></div>
      <article className="max-w-3xl mx-auto px-6 md:px-12 py-20 prose-legal" data-testid="legal-article">
        <p className="tcp-overline text-[#C29B62] mb-4">{overline}</p>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tighter leading-none text-[#1C1C1C] mb-10">{title}</h1>
        <p className="text-xs text-[#666460] mb-12">Last updated: January 2026</p>
        <div className="space-y-8 text-[#1C1C1C] leading-relaxed text-[15px]">
          {children}
        </div>
      </article>
      <Footer />
    </div>
  );
}

function H2({ children }) { return <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#1C1C1C] mt-10 mb-3">{children}</h2>; }
function P({ children }) { return <p className="text-[#1C1C1C] leading-relaxed">{children}</p>; }

export function Privacy() {
  return (
    <LegalLayout overline="Legal" title="Privacy Policy">
      <P>This Privacy Policy explains how TheCityPair LLC ("we," "us," or "our") collects, uses, and protects personal information when you use TheCityPair.com.</P>
      <H2>1. Information We Collect</H2>
      <P>We collect: (a) information you provide directly — name, email, phone, passport number, payment details, travel preferences; (b) information collected automatically — IP address, browser type, device identifiers, pages visited; and (c) information from partners — airlines, GDS providers, and payment processors to confirm and service your reservations.</P>
      <H2>2. How We Use Information</H2>
      <P>We use personal data to process bookings, communicate confirmations and travel alerts, provide customer support, comply with legal obligations, prevent fraud, and improve our services. We do not sell personal data.</P>
      <H2>3. Cookies &amp; Third-Party Tracking</H2>
      <P>We use cookies, pixels, and similar technologies including Google Analytics 4 and Google Tag Manager to measure performance, prevent fraud, and (with consent) deliver relevant marketing. See our Cookie Policy for details.</P>
      <H2>4. Your Rights (GDPR &amp; CCPA)</H2>
      <P>If you are a resident of the EEA, UK, or California you have the right to access, correct, delete, restrict, and port your personal data, and to object to certain processing. California residents have the right to opt out of "sale" or "sharing" of personal information; we do not sell personal data. To exercise these rights, contact privacy@thecitypair.com.</P>
      <H2>5. Data Retention</H2>
      <P>We retain booking records for seven (7) years to comply with US tax and travel-industry obligations. Marketing data is retained until you unsubscribe.</P>
      <H2>6. Children</H2>
      <P>Our services are not directed to children under 13. We do not knowingly collect data from children under 13.</P>
      <H2>7. International Transfers</H2>
      <P>Your data may be processed outside the United States. Where required we rely on Standard Contractual Clauses or equivalent safeguards.</P>
      <H2>8. Contact</H2>
      <P>Data Protection Officer · privacy@thecitypair.com · 1736 Holly Oak Ln, Chattanooga, TN 37421, USA.</P>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout overline="Legal" title="Terms & Conditions">
      <P>By accessing TheCityPair.com or making a reservation through us, you agree to these Terms.</P>
      <H2>1. Booking Terms</H2>
      <P>TheCityPair LLC acts as an authorized agent for airlines and other travel suppliers. The contract of carriage is between you and the operating supplier. All fares are subject to availability and are not guaranteed until ticketed.</P>
      <H2>2. Pricing &amp; Payment</H2>
      <P>All prices are displayed in US Dollars (USD) and include applicable taxes, fees, and our service fee unless explicitly stated otherwise. Payment is processed securely via PCI-DSS compliant providers.</P>
      <H2>3. Cancellations &amp; Refunds</H2>
      <P>Refundability is governed by the airline's fare rules. We offer free cancellation within 24 hours of booking for flights departing more than 7 days from purchase, per US DOT 14 CFR 259.5(b)(4). Non-refundable fares forfeit fare value upon cancellation; taxes may be reclaimable.</P>
      <H2>4. User Responsibilities</H2>
      <P>You are responsible for the accuracy of all passenger details, valid travel documents (passport, visa, vaccination), and arriving at the airport in time for check-in. Name changes after ticketing are restricted by airline policy.</P>
      <H2>5. Limitation of Liability</H2>
      <P>To the maximum extent permitted by law, TheCityPair LLC's aggregate liability under any booking shall not exceed the service fee paid to us. We are not liable for supplier insolvency, schedule changes, denied boarding, lost baggage, or force majeure events.</P>
      <H2>6. Prohibited Use</H2>
      <P>You may not use our service for fraudulent activity, including credit-card chargeback abuse, fictitious booking, or violation of fare rules.</P>
      <H2>7. Governing Law</H2>
      <P>These Terms are governed by the laws of the State of Tennessee. Disputes shall be resolved through binding arbitration in Hamilton County, TN, except where prohibited by consumer law.</P>
      <H2>8. Changes</H2>
      <P>We may update these Terms; the latest version applies to bookings made after publication.</P>
    </LegalLayout>
  );
}

export function Cookies() {
  return (
    <LegalLayout overline="Legal" title="Cookie Policy">
      <P>Cookies are small text files placed on your device when you visit a website. They help us deliver, secure, and improve our services.</P>
      <H2>1. Categories We Use</H2>
      <P><strong>Strictly Necessary</strong> — session, authentication, fraud prevention. Cannot be disabled.<br/>
      <strong>Performance</strong> — Google Analytics 4, page-load measurement. Anonymized.<br/>
      <strong>Functional</strong> — language and currency preferences, saved searches.<br/>
      <strong>Marketing</strong> — Google Ads conversion, Meta Pixel, remarketing. Loaded only with your consent.</P>
      <H2>2. Managing Cookies</H2>
      <P>Most browsers allow you to control cookies via settings. You can also opt out of Google Analytics via the <a href="https://tools.google.com/dlpage/gaoptout" className="text-[#C29B62] underline">Google opt-out add-on</a>, and out of personalized ads via <a href="https://www.youradchoices.com" className="text-[#C29B62] underline">YourAdChoices</a>.</P>
      <H2>3. Do Not Track</H2>
      <P>Our website respects the Global Privacy Control (GPC) signal where applicable under California law.</P>
      <H2>4. Updates</H2>
      <P>We review our cookie practices quarterly. Material changes will trigger a fresh consent prompt.</P>
    </LegalLayout>
  );
}

export function DataProtection() {
  return (
    <LegalLayout overline="Legal" title="Data Protection">
      <H2>1. Security Measures</H2>
      <P>All traffic is encrypted in transit with TLS 1.3. Payment data is tokenized and never stored on our servers. Production databases are encrypted at rest (AES-256). Access is restricted via role-based controls, multi-factor authentication, and audited via SIEM.</P>
      <H2>2. Storage Locations</H2>
      <P>Personal data is stored in SOC 2 Type II certified facilities in US-East and US-West regions. Backups are encrypted and retained for 35 days.</P>
      <H2>3. Personnel Practices</H2>
      <P>Employees receive annual security and privacy training. Background checks are performed on all staff with access to production systems.</P>
      <H2>4. Incident Response</H2>
      <P>In the unlikely event of a data breach affecting personal information, we will notify affected users within 72 hours of confirmation, as required by GDPR Article 33 and applicable US state breach-notification laws.</P>
      <H2>5. Data Retention &amp; Disposal</H2>
      <P>Booking records: 7 years. Marketing data: until unsubscribe. Logs: 1 year. Data is securely overwritten upon expiry.</P>
      <H2>6. Subject Requests</H2>
      <P>To exercise data subject rights, write to privacy@thecitypair.com. We respond within 30 days.</P>
    </LegalLayout>
  );
}
